/**
 * Notification Mentions Service
 *
 * Mirrors Flutter's NotificationsRepository.processCommentMentionsNotifications.
 * Walks comment text for `@username` tokens, looks up each user, and writes
 * an AppNotification with notificationType = commentMention to each.
 */

import { firestoreService } from '@/data/services/firestoreService';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { getUserByUsernameUseCase } from '@/domain/usecases/user/getUserByUsernameUseCase';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { NotificationInfoRemapper } from '@/domain/remappers/notificationInfo/notificationInfoRemapper';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';

export class NotificationMentionsService {
  private static instance: NotificationMentionsService;

  public static getInstance(): NotificationMentionsService {
    if (!NotificationMentionsService.instance) {
      NotificationMentionsService.instance = new NotificationMentionsService();
    }
    return NotificationMentionsService.instance;
  }

  async processCommentMentions(
    commentText: string,
    baseNotificationInfo: NotificationInfoDto,
  ): Promise<void> {
    const words = commentText.split(' ');
    for (const word of words) {
      if (!word) continue;
      const decorationIndex = word.indexOf('@');
      if (decorationIndex < 0) continue;

      const decorationWord = word.substring(decorationIndex + 1);
      const mentions = decorationWord.split('@');
      for (const mention of mentions) {
        if (!mention) continue;
        try {
          const user = await getUserByUsernameUseCase.execute(mention);
          if (!user?.firebaseId) continue;

          const notificationInfo: NotificationInfoDto = {
            usersInfo: {
              userId: baseNotificationInfo.usersInfo.userId,
              peerUserId: user.firebaseId,
              name: baseNotificationInfo.usersInfo.name,
              username: baseNotificationInfo.usersInfo.username,
            },
            mediaInfo: baseNotificationInfo.mediaInfo,
            commentInfo: baseNotificationInfo.commentInfo,
            notificationType: AppNotificationType.commentMention,
            read: false,
            isUndo: false,
            timestamp: Date.now(),
          };

          const notificationId = generateUuidV1();
          await firestoreService.setUserNotification(
            user.firebaseId,
            notificationId,
            NotificationInfoRemapper.toJson(notificationInfo),
          );
        } catch (error) {
          appLogger.error(
            'Error processing comment mention',
            'NotificationMentionsService', { error: error as Error },
          );
        }
      }
    }
  }
}

export const notificationMentionsService = NotificationMentionsService.getInstance();
